import { useEffect, useRef } from 'react';
import { env } from '../../config/env';
import type { GoogleCredentialResponse } from '../../types/auth';

interface GoogleLoginButtonProps {
  onSuccess: (credential: string) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  text?: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: unknown) => void;
          renderButton: (element: HTMLElement, config: unknown) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export function GoogleLoginButton({
  onSuccess,
  onError,
  disabled = false,
  text = 'Sign in with Google',
}: GoogleLoginButtonProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const scriptLoaded = useRef(false);

  useEffect(() => {
    const currentButtonRef = buttonRef.current;
    
    const loadGoogleScript = () => {
      if (scriptLoaded.current || window.google) {
        initializeGoogle();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        scriptLoaded.current = true;
        initializeGoogle();
      };
      script.onerror = () => {
        // Failed to load Google Sign-In script
        onError?.('Failed to load Google Sign-In script');
      };
      document.head.appendChild(script);
    };

    const initializeGoogle = () => {
      if (!window.google || !buttonRef.current) {
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: env.GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        // Get the actual width of the container
        const containerWidth = buttonRef.current.offsetWidth || 400;
        
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          logo_alignment: 'left',
          width: Math.min(containerWidth, 400),
        });
      } catch {
        // Error initializing Google Sign-In
        onError?.('Failed to initialize Google Sign-In');
      }
    };

    const handleCredentialResponse = (response: GoogleCredentialResponse) => {
      if (response.credential) {
        onSuccess(response.credential);
      } else {
        // No credential received from Google
        onError?.('No credential received from Google');
      }
    };

    if (!disabled) {
      loadGoogleScript();
    }

    return () => {
      // Cleanup if needed
      if (currentButtonRef) {
        currentButtonRef.innerHTML = '';
      }
    };
  }, [onSuccess, onError, disabled]);

  if (disabled) {
    return (
      <button
        disabled
        className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-400 cursor-not-allowed"
      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        {text}
      </button>
    );
  }

  return (
    <div className="w-full flex justify-center">
      <div 
        ref={buttonRef} 
        className="w-full max-w-[400px] [&>div]:!w-full [&_iframe]:!w-full"
      />
    </div>
  );
}